import {
  AssessmentData,
  AssessmentType,
  checkFirstResponseIfNeeded,
  ResponseData,
} from '@playbooks/action-cards-utils/src'
import { SingleSelect } from './components/SingleSelect'
import { ArrowRight } from 'lucide-react'
import { Badge } from '@playbooks/ui/components/badge'
import { Switch } from '@playbooks/ui/components/switch'
import { MultiSelect } from './components/MultiSelect'
import { Input } from '@playbooks/ui/components/input'
import { cn } from '@playbooks/ui/lib/utils'
import useVars from '../../hooks/useVars'
import MarkdownText from '../../components/MarkdownText'
import Highlighter from '../../components/Highlighter'
import { useAppContext } from '../../contexts/AppContext'

interface AssessmentProps {
  data: AssessmentData
}

export default function Assessment({ data }: AssessmentProps) {
  const { update, resetHighlights } = useAppContext()

  const handleAssessmentEnabled = () => {
    if (!data.isAutoCollapse) return
    // We have to manually toggle the checked state here
    // because the checkFirstResponseIfNeeded needs the actual state
    // of the assessment data
    data.checked = !data.checked

    checkFirstResponseIfNeeded(data)
    update()
  }

  const showTreeText = !!(data.responses[data.responses.length - 1]?.checked && data.hasFreeText)

  const handleSingleSelectValuesChange = (response: ResponseData) => {
    data.responses.forEach(option => (option.checked = false))
    update(response, 'checked', !response.checked)
  }

  const handleMultiSelectValuesChange = (response: ResponseData) => {
    update(response, 'checked', !response.checked)
  }

  const handleFreeTextChange = (value: string) => {
    update(data, 'freeTextValue', value)
  }

  const isSingleSelect = data.type === AssessmentType.SINGLE
  const SelectComponent = isSingleSelect ? SingleSelect : MultiSelect

  const handleValueChange = (response: ResponseData) => {
    resetHighlights([data.id])
    if (isSingleSelect) {
      handleSingleSelectValuesChange(response)
    } else {
      handleMultiSelectValuesChange(response)
    }
  }
  const shouldRenderSelect = !data.isAutoCollapse || data.checked

  return (
    <div className={cn('AC_assessment w-full', data.type === AssessmentType.SINGLE ? 'AC_single' : 'AC_multi')}>
      <div
        className={cn(
          'flex items-center mb-2 text-left',
          'group-data-[view=funnel]/app:gap-2',
          'group-data-[view=funnel]/app:flex-row-reverse',
          'group-data-[view=funnel]/app:justify-between',
        )}
        onClick={handleAssessmentEnabled}
      >
        <div className="relative shrink-0 self-start -left-2 group-data-[view=funnel]/app:left-0">
          {data.isAutoCollapse ? (
            <Switch checked={data.checked} className="relative top-0.5 h-5 group-data-[view=funnel]/app:top-1.25" />
          ) : (
            <Badge className="group-data-[view=funnel]/app:hidden">
              <div className="px-0.75">
                <ArrowRight className="size-3" />
              </div>
            </Badge>
          )}
        </div>

        <h2
          className={cn(
            'text-foreground',
            'group-data-[view=funnel]/app:text-lg',
            'group-data-[view=funnel]/app:font-medium',
          )}
        >
          <Question assessment={data} />
        </h2>
      </div>

      {shouldRenderSelect && (
        <>
          <SelectComponent assessment={data} responses={data.responses} onValueChange={handleValueChange} />
          {showTreeText && (
            <div className="AC_free-text mt-4">
              <Input
                placeholder="Type your answer here..."
                value={data.freeTextValue ?? ''}
                onChange={e => handleFreeTextChange(e.target.value)}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Question({ assessment }: { assessment: AssessmentData }) {
  const question = useVars(assessment.question)
  const { settings } = useAppContext()

  return (
    <Highlighter id={assessment.id} forAssessment>
      <span
        data-tags={JSON.stringify(assessment.tags?.map(t => t.id))}
        className={cn(assessment.isAutoCollapse && 'cursor-pointer')}
      >
        <MarkdownText text={question || ''} className={cn('')} />

        <span className="font-normal">{settings?.showMandatoryFields && assessment.required && '*'}</span>
      </span>
    </Highlighter>
  )
}
